import { z } from 'zod';
import { currencySchema, moneySchema } from './common.js';

/**
 * Payment contracts. CLAUDE.md §3 `payment` — a provider interface with
 * `manual_qr` as the zero-paperwork default, plus the three-rung confirmation
 * ladder that runs on top of it.
 */

export const paymentProviderSchema = z.enum(['manual_qr', 'bakong_api', 'aba_payway', 'cod']);
export type PaymentProviderId = z.infer<typeof paymentProviderSchema>;

export const paymentStatusSchema = z.enum([
  'pending',
  'confirmed',
  'expired',
  'failed',
  'refunded',
  'cancelled',
]);

export const paymentConfirmedBySchema = z.enum([
  'manual',
  'forwarded_notification',
  'group_listener',
  'provider_webhook',
  'cod_collection',
]);

export const paymentSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  provider: paymentProviderSchema,
  method: z.enum(['khqr', 'aba_pay', 'card', 'cod']),
  status: paymentStatusSchema,
  amount: moneySchema,
  /** The exact amount to transfer, including the disambiguating offset. */
  chargeAmount: moneySchema,
  qrPayload: z.string().nullable(),
  deepLink: z.string().nullable(),
  payeeLabel: z.string().nullable(),
  externalRef: z.string().nullable(),
  confirmedBy: paymentConfirmedBySchema.nullable(),
  confirmedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type PaymentDto = z.infer<typeof paymentSchema>;

/* ------------------------------------------------------------------ */
/* ABA notification parsing                                            */
/* ------------------------------------------------------------------ */

/**
 * Shape produced by the ABA parser. Captured official format (§3):
 *
 *   $12.65 paid by LORN VICHANA (*190) on Aug 15, 10:46 AM via ABA PAY
 *   at VILLA COFFEE by S.L. Trx. ID: 178676560243040, APV: 247087.
 *
 * `trxId` is the dedup key and the reconciliation reference. The year is
 * absent from the text and inferred (Dec→Jan boundary handled in `shared`).
 */
export const abaNotificationSchema = z.object({
  amount: z.number().int(),
  currency: currencySchema,
  payerName: z.string().nullable(),
  maskedAccount: z.string().nullable(),
  occurredAt: z.string().datetime(),
  /** "ABA PAY", "KHQR", "ABA Mobile" … kept raw for future format drift. */
  method: z.string().nullable(),
  merchantLabel: z.string().nullable(),
  trxId: z.string(),
  apv: z.string().nullable(),
  rawText: z.string(),
});
export type AbaNotificationDto = z.infer<typeof abaNotificationSchema>;

export const abaMatchStatusSchema = z.enum([
  'unmatched',
  'matched',
  'duplicate',
  'unparsed',
  'ignored',
]);

/* ------------------------------------------------------------------ */
/* Provider configuration                                              */
/* ------------------------------------------------------------------ */

/** Per-shop, opt-in, credentials encrypted at rest like bot tokens (§3). */
export const bakongCredentialsSchema = z.object({
  /** Bakong Open API bearer token issued to the merchant. */
  accessToken: z.string().min(10),
  accountId: z.string().min(3).max(64),
  merchantName: z.string().min(1).max(25),
  merchantCity: z.string().min(1).max(15).default('Phnom Penh'),
  baseUrl: z.string().url().default('https://api-bakong.nbc.gov.kh'),
});

export const payWayCredentialsSchema = z.object({
  merchantId: z.string().min(1).max(40),
  apiKey: z.string().min(8),
  baseUrl: z.string().url().default('https://checkout.payway.com.kh'),
});

export const providerConfigSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('manual_qr'), enabled: z.boolean() }),
  z.object({
    provider: z.literal('bakong_api'),
    enabled: z.boolean(),
    credentials: bakongCredentialsSchema,
  }),
  z.object({
    provider: z.literal('aba_payway'),
    enabled: z.boolean(),
    credentials: payWayCredentialsSchema,
  }),
]);
export type ProviderConfigInput = z.infer<typeof providerConfigSchema>;

export const providerStatusSchema = z.object({
  provider: paymentProviderSchema,
  enabled: z.boolean(),
  health: z.enum(['ok', 'degraded', 'down']),
  lastCheckAt: z.string().datetime().nullable(),
  message: z.string().nullable(),
});

/* ------------------------------------------------------------------ */
/* Merchant / listener inputs                                          */
/* ------------------------------------------------------------------ */

/** Rung 2: merchant forwards the ABA Merchant bot message into our bot. */
export const forwardedNotificationSchema = z.object({
  text: z.string().min(10).max(1000),
  shopId: z.string().uuid(),
  telegramMessageId: z.number().int().optional(),
});

/** Rung 3 onboarding: merchant tells us which group carries ABA alerts. */
export const abaGroupSetupSchema = z.object({
  chatId: z.number().int(),
  title: z.string().max(120).optional(),
});

export const manualConfirmSchema = z.object({
  paymentId: z.string().uuid(),
  note: z.string().max(200).optional(),
});

export const refundSchema = z.object({
  orderId: z.string().uuid(),
  amount: z.number().int().min(1),
  currency: currencySchema,
  reason: z.string().max(200),
  method: z.enum(['khqr_manual', 'cash', 'provider']).default('khqr_manual'),
  reference: z.string().max(80).optional(),
});

/* ------------------------------------------------------------------ */
/* COD remittance                                                      */
/* ------------------------------------------------------------------ */

export const codRemittanceSchema = z.object({
  carrier: z.string().max(60),
  batchRef: z.string().max(60),
  amount: z.number().int().min(1),
  currency: currencySchema,
  receivedAt: z.string().datetime(),
  orderIds: z.array(z.string().uuid()).min(1).max(500),
  note: z.string().max(300).optional(),
});
